// src/components/TopNav.jsx
import React, { useState } from "react";
import {
    Search,
    ChevronDown,
    Activity,
    MapPin,
    Radar,
    RefreshCcw,
    ShoppingCart,
    FolderOpen,
    Sparkle,
    Ruler,
    MessageSquare,
    BarChart3,
    ClipboardCheck,
    LandPlot
} from "lucide-react";

import { useSiteStore } from "../stores/useSiteStore";
import { useProjectStore } from "../stores/useProjectStore";
import { useAppNav } from "../hooks/useAppNav";
import { MAP_PRODUCT_TABS } from '../constants/mapTabs'
import { useCollabRailStore } from "../stores/useCollabRailStore";
import { useCollaborationStore } from "../stores/useCollaborationStore";
import { useAnalyticsProductsStore } from "../stores/useAnalyticsProductsStore";
import MapOperatingAoiPicker from "./MapOperatingAoiPicker";

import { MODE_TO_PRODUCT, UNGATED_MODES, PORTAL_PRODUCT, PADPLANNER_PRODUCT, PADPLANNER_DISPLAY_NAME } from "../constants/analyticsProducts";

export default function TopNav({
    // ── routing ──
    showModes = false,   // only true when MapPage / map tab is active

    // ── filters ──
    basin,
    setBasin,
    operator,
    setOperator,
    assetType,
    setAssetType,
}) {
    const activeProjectId = useProjectStore(s => s.activeProjectId);
    const projects = useProjectStore(s => s.projects);
    const isGlobal = useProjectStore(s => s.isGlobal);
    const setActiveProjectId = useProjectStore(s => s.setActiveProjectId);

    const siteIdEnabled = useSiteStore(s => s.siteIdEnabled);
    const toggleSiteId = useSiteStore(s => s.toggleSiteId);
    const setActiveProject = useSiteStore(s => s.setActiveProject);
    const clearSelection = useSiteStore(s => s.clearSelection);
    const clearFocusedFeature = useSiteStore(s => s.clearFocusedFeature);


    const nav = useAppNav()
    // On /padplanner no map tab is active. Without the null, activeTab's
    // fallback would light Map beside PadPlanner.
    const activeMode = nav.isPadPlanner ? null : (nav.activeTab ?? 'explore')

    const selectedProjectName = isGlobal
        ? null
        : (projects.find(p => p.id === activeProjectId)?.name ?? null);

    const MODE_META = {
        explore: { label: 'Map', icon: MapPin },
        well_finder: { label: 'WellFinder', icon: Radar },
        siteid: { label: 'AssetID', icon: MapPin },
        padrekko: { label: 'PadRekko', icon: RefreshCcw },
        helios: { label: 'AI On-demand', icon: Sparkle },
        purchase: { label: 'Purchase Imagery', icon: ShoppingCart },
    };

    const entitledProductCodes = useAnalyticsProductsStore((s) => s.productCodes);
    // Same set, same rules as the modes strip below — there is no second
    // opinion about who holds the portal.
    const portalEntitled = entitledProductCodes.has(PORTAL_PRODUCT);
    const padplannerEntitled = entitledProductCodes.has(PADPLANNER_PRODUCT);
    const productsLoaded = useAnalyticsProductsStore((s) => s.loaded);

    const MODES = MAP_PRODUCT_TABS.map((id) => ({ id, ...MODE_META[id] }))
        .filter((mode) => {
            if (UNGATED_MODES.has(mode.id)) return true;
            const productCode = MODE_TO_PRODUCT[mode.id];
            if (!productCode) return true;  // unknown mode, don't gate
            return entitledProductCodes.has(productCode);
        });

    // Purchase Imagery lives in its own strip next to Purchase Analytics,
    // separate from the product-mode strip (Map/WellFinder/AssetID/PadRekko/…).
    const mapModes = MODES.filter((m) => m.id !== "purchase");
    const purchaseMode = MODES.find((m) => m.id === "purchase");

    // ── PORTAL — IN THE MODES STRIP, BUT NOT A MODE ────────────────────────
    //
    // It renders inside the modes strip and matches its buttons, because to a
    // reviewer it is one product among the ones their org holds. It is NOT a
    // MODE_META entry, for exactly the reason the Route control below is not:
    // that list navigates map TABS through goMapTab(), and /portal is a
    // top-level route under its own layout. Listed there it would look like a
    // tab and route like one.
    //
    // GATED, as of migration 120. It was ungated when it landed because no
    // product code existed for it; aiprime.analytics_products now carries a
    // PORTAL row, so this is filtered on the SAME entitled-codes set that
    // filters WellFinder, AssetID and PadRekko above -- including BUNDLE_ALL,
    // which both readers expand from the table at request time.
    //
    // It sits here rather than in the MODES filter because it is not a map tab.
    // The filter runs over MAP_PRODUCT_TABS and routes through goMapTab().
    const renderPortalButton = () => (
        <button
            key="portal"
            type="button"
            onClick={() => nav.goPortal()}
            title="AI PRIME SurfacePlanner"
            aria-current={nav.isPortal ? "page" : undefined}
            className={`px-2 py-1 text-xs rounded-md transition ${nav.isPortal
                ? "text-accent-action border border-accent-action/40"
                : "text-gray-400 hover:text-gray-200"
                }`}
        >
            <div className="flex items-center gap-1.5">
                <ClipboardCheck className="w-3.5 h-3.5 shrink-0" />
                <span>SurfacePlanner</span>
            </div>
        </button>
    );

    // ── PADPLANNER — LAST IN THE MODES STRIP, NOT A MODE ───────────────────
    //
    // Not a MODE_META entry for the portal's reason: /padplanner is a
    // top-level route, not a map tab. Gated on PADPLANNER in the same
    // entitled-codes set, and placed after the last map mode below.
    const renderPadPlannerButton = () => (
        <button
            key="padplanner"
            type="button"
            onClick={() => nav.goPadPlanner()}
            title={`AI PRIME ${PADPLANNER_DISPLAY_NAME}`}
            aria-current={nav.isPadPlanner ? "page" : undefined}
            className={`px-2 py-1 text-xs rounded-md transition ${nav.isPadPlanner
                ? "text-accent-action border border-accent-action/40"
                : "text-gray-400 hover:text-gray-200"
                }`}
        >
            <div className="flex items-center gap-1.5">
                <LandPlot className="w-3.5 h-3.5 shrink-0" />
                <span>{PADPLANNER_DISPLAY_NAME}</span>
            </div>
        </button>
    );

    const renderModeButton = (mode) => {
        const isActive = activeMode === mode.id;
        return (
            <button
                key={mode.id}
                onClick={() => {
                    const isSiteId = mode.id === "siteid";
                    if (isSiteId && !siteIdEnabled) {
                        setActiveProject(activeProjectId);
                        toggleSiteId();
                    } else if (!isSiteId && siteIdEnabled) {
                        // Leaving AssetID: clear the selection before
                        // switching modes so no asset stays selected.
                        clearSelection();
                        clearFocusedFeature();
                        toggleSiteId();
                    }
                    nav.goMapTab(mode.id)
                }}
                className={`px-2 py-1 text-xs rounded-md transition ${isActive
                    ? "text-accent-action border border-accent-action/40"
                    : "text-gray-400 hover:text-gray-200"
                    }`}
            >
                <div className="flex items-center gap-1.5">
                    {mode.icon && <mode.icon className="w-3.5 h-3.5 shrink-0" />}
                    <span>{mode.label}</span>
                </div>
            </button>
        );
    };

    const [openMenu, setOpenMenu] = useState(null);
    const [search, setSearch] = useState("");

    const basins = ["Permian", "Delaware", "Eagle Ford", "Bakken", "Powder River"];
    const operators = ["All Operators", "Oxy", "ConocoPhillips", "Diamondback", "Coterra"];
    const assetTypes = ["Pads", "Wells", "Tanks", "Flowlines", "Facilities"];

    const toggleMenu = (name) => setOpenMenu(openMenu === name ? null : name);

    const { open: collabOpen, toggleRail } = useCollabRailStore();
    const totalUnread = useCollaborationStore((s) => s.unreadCount);

    return (
        <nav className="h-11 flex items-center justify-between px-4 border-b border-slate-800 bg-bg-soft pointer-events-none">

            {/* ── LEFT ── */}
            <div className="flex items-center gap-3 pointer-events-auto">

                {showModes && (
                    <>

                        {/* OPERATING AOI PICKER. NOT ON /padplanner: a plan has no
                            AOI, so on PadPlanner it would filter nothing. The
                            project picker stays: it filters plans (148). */}
                        {!nav.isPadPlanner && <MapOperatingAoiPicker />}

                        {/* PROJECT PICKER */}
                        <ProjectPicker
                            selectedName={selectedProjectName}
                            isGlobal={isGlobal}
                            projects={projects}
                            open={openMenu === "project"}
                            onToggle={() => toggleMenu("project")}
                            onSelect={(p) => {
                                setActiveProjectId(p === "global" ? "global" : p.id);
                                setOpenMenu(null);
                            }}
                        />

                        <div className="h-5 w-px bg-slate-700" />

                        {/* MODES STRIP */}
                        <div className="flex items-center gap-0.5 bg-bg border border-slate-700 rounded-md p-0.5">
                            {!productsLoaded ? (
                                // Entitlements still loading — render placeholder pills rather than
                                // the productCode-filtered list. Filtering on an empty productCodes
                                // set would flash a wrong/empty mode strip (and, on a failed load,
                                // silently hide gated modes with no signal).
                                [0, 1, 2, 3].map((i) => (
                                    <div key={`mode-skel-${i}`} className="px-2 py-1" aria-hidden="true">
                                        <div className="h-3.5 w-16 rounded bg-slate-700/50 animate-pulse" />
                                    </div>
                                ))
                            ) : [
                                ...mapModes.flatMap((mode) =>
                                    // RIGHT AFTER MAP, by the mode's id rather than
                                    // by an index -- the list is entitlement-filtered,
                                    // so position 0 is not reliably 'explore' and a
                                    // splice at 1 would land somewhere different for
                                    // an org without WellFinder.
                                    mode.id === "explore" && portalEntitled
                                        ? [renderModeButton(mode), renderPortalButton()]
                                        : [renderModeButton(mode)],
                                ),
                                // LAST, after whichever map mode the filter left last.
                                ...(padplannerEntitled ? [renderPadPlannerButton()] : []),
                            ]}
                        </div>

                        {/* PURCHASE STRIP — own container matching the modes strip */}
                        <div className="flex items-center gap-0.5 bg-bg border border-slate-700 rounded-md p-0.5">
                            {purchaseMode && renderModeButton(purchaseMode)}

                            {/* PURCHASE ANALYTICS — placeholder; action wired later */}
                            <button
                                type="button"
                                title="Purchase Analytics"
                                className="px-2 py-1 text-xs rounded-md text-gray-400 hover:text-gray-200 transition"
                            >
                                <div className="flex items-center gap-1.5">
                                    <BarChart3 className="w-3.5 h-3.5 shrink-0" />
                                    <span>Purchase Analytics</span>
                                </div>
                            </button>
                        </div>


                        {/* FILTERS */}
                        {/*
                        <div className="h-5 w-px bg-slate-700" />

                        <FilterDropdown
                            label="Basin" value={basin} items={basins}
                            open={openMenu === "basin"} onClick={() => toggleMenu("basin")}
                            onSelect={(v) => { setBasin(v); setOpenMenu(null); }}
                        />
                        <FilterDropdown
                            label="Operator" value={operator} items={operators}
                            open={openMenu === "operator"} onClick={() => toggleMenu("operator")}
                            onSelect={(v) => { setOperator(v); setOpenMenu(null); }}
                        />
                        <FilterDropdown
                            label="Asset" value={assetType} items={assetTypes}
                            open={openMenu === "asset"} onClick={() => toggleMenu("asset")}
                            onSelect={(v) => { setAssetType(v); setOpenMenu(null); }}
                        />
                        */}

                        {/*       
                        <div className="h-5 w-px bg-slate-700" />
                        */}

                    </>
                )}
            </div>

            {/* ── RIGHT ── */}
            <div className="flex items-center gap-4 pointer-events-auto">
                {/*
                <div className="relative">
                    <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-fg-secondary" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        type="text"
                        placeholder="Search wells, operators, assets..."
                        className="bg-bg w-64 pl-7 pr-2 py-1.5 rounded-md border border-slate-700
                        focus:border-accent-info/60 text-xs text-gray-200 placeholder-gray-500
                        outline-none transition"
                    />
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-400">
                    <Activity size={14} className="text-accent-info animate-pulse" />
                    <span className="uppercase tracking-wider">System OK</span>
                </div>
                */}
                {/* NOT ON /padplanner. <CollaborationRail /> mounts only in MapPage,
                    so on PadPlanner this button would flip useCollabRailStore and
                    nothing would listen: the portal's reason, App.jsx above TopNav. */}
                {!nav.isPadPlanner && (
                <button
                    onClick={toggleRail}
                    title={collabOpen ? "Close collaboration" : "Open collaboration"}
                    className={`relative flex items-center justify-center w-7 h-7 rounded-md border text-xs transition
                        ${collabOpen
                            ? "border-sky-500/40 bg-sky-500/10 text-sky-400"
                            : "border-slate-700 bg-bg text-zinc-400 hover:text-zinc-200 hover:border-slate-600"}
                        `}
                >
                    <MessageSquare size={13} />
                    {totalUnread > 0 && (
                        <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-sky-500 text-white text-[8px] font-bold flex items-center justify-center leading-none">
                            {totalUnread > 9 ? "9+" : totalUnread}
                        </span>
                    )}
                </button>
                )}
            </div>
        </nav>
    );
}

/* ── ProjectPicker ───────────────────────────────────────────────────────── */
function ProjectPicker({ selectedName, isGlobal, projects, open, onToggle, onSelect }) {
    return (
        <div className="relative">
            <button
                onClick={onToggle}
                className={`
                    flex items-center gap-1.5 h-7 px-2.5 rounded-md border text-xs transition
                    ${isGlobal
                        ? "border-yellow-500/40 bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/15"
                        : "border-accent-action/40 text-accent-action hover:bg-accent-action/10"}
                `}
            >
                <FolderOpen size={12} className="shrink-0" />
                <span className="font-medium max-w-[160px] truncate">
                    {isGlobal ? "Select project…" : selectedName}
                </span>
                <ChevronDown size={11} className="opacity-60 shrink-0" />
            </button>

            {open && (
                <div className="absolute top-full mt-1 left-0 w-64 bg-bg-soft border border-slate-700
                    rounded-md shadow-xl z-50 overflow-hidden">
                    <div
                        onClick={() => onSelect("global")}
                        className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer
                            hover:bg-bg text-gray-400 hover:text-gray-200 border-b border-slate-800 transition"
                    >
                        <span className="w-2 h-2 rounded-full bg-slate-600 shrink-0" />
                        No project (global)
                    </div>
                    {projects.length === 0 ? (
                        <div className="px-3 py-3 text-xs text-fg-secondary italic">
                            No projects available
                        </div>
                    ) : (
                        projects.map((p, i) => {
                            const colors = ["#1f9dff", "#16f2d4", "#facc15", "#a78bfa"];
                            return (
                                <div
                                    key={p.id}
                                    onClick={() => onSelect(p)}
                                    className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer
                                        hover:bg-bg text-gray-300 hover:text-gray-100 transition
                                        border-b border-slate-800/60 last:border-b-0"
                                >
                                    <span
                                        className="w-2 h-2 rounded-sm shrink-0"
                                        style={{ background: colors[i % colors.length] }}
                                    />
                                    <span className="flex-1 truncate">{p.name}</span>
                                    {p.role && (
                                        <span className="text-[9px] text-fg-secondary font-mono uppercase tracking-wide shrink-0">
                                            {p.role}
                                        </span>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
}

/* ── FilterDropdown ──────────────────────────────────────────────────────── */
function FilterDropdown({ label, value, items, open, onClick, onSelect, getLabel = (v) => String(v) }) {
    return (
        <div className="relative">
            <button
                onClick={onClick}
                className="flex items-center gap-1 px-2 py-1.5 text-xs rounded-md bg-bg border border-slate-700
                   text-gray-300 hover:border-accent-action/60 hover:text-accent-action transition"
            >
                <span className="font-mono uppercase tracking-wider text-[10px] text-gray-400">{label}</span>
                <span className="font-medium">{value}</span>
                <ChevronDown size={14} className="text-fg-secondary" />
            </button>
            {open && (
                <div className="absolute mt-1 w-56 bg-bg-soft border border-slate-700 rounded-md shadow-xl z-50">
                    {items.map((item) => (
                        <div
                            key={item.slug ?? getLabel(item)}
                            onClick={() => onSelect(item)}
                            className="px-3 py-2 text-xs text-gray-300 hover:bg-bg cursor-pointer"
                        >
                            {getLabel(item)}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}